import { createContext, useContext, useState, useEffect, useCallback } from "react";
import { api } from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [orgs, setOrgs] = useState([]);
  const [currentOrgId, setCurrentOrgId] = useState(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data.user);
      setOrgs(data.organizations || []);
      setCurrentOrgId(data.current_org_id);
      return data;
    } catch (e) {
      setUser(null);
      setOrgs([]);
      setCurrentOrgId(null);
      return null;
    }
  }, []);

  useEffect(() => {
    // If returning from OAuth callback, skip the /me check.
    // AuthCallback handles exchange first.
    if (window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    (async () => {
      await refresh();
      setLoading(false);
    })();
  }, [refresh]);

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch (_e) { /* ignore */ }
    setUser(null);
    setOrgs([]);
    setCurrentOrgId(null);
  };

  const switchOrg = async (orgId) => {
    await api.post(`/organizations/${orgId}/switch`);
    setCurrentOrgId(orgId);
  };

  const updateProfile = async (name) => {
    const { data } = await api.patch("/user/profile", { name });
    setUser((u) => (u ? { ...u, name: data.name } : u));
    return data;
  };

  const currentOrg = orgs.find((o) => o.org_id === currentOrgId) || null;

  return (
    <AuthContext.Provider value={{
      user, orgs, currentOrg, currentOrgId, loading,
      refresh, logout, switchOrg, updateProfile, setUser,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
};
